import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { comparisonRange, formatDateRange, matchingRangeKey, rangeDays } from '@promanage/core';
import { TopBar } from '../components/TopBar';
import { LoadFailed } from '../components/QueryState';
import { DashboardControls } from '../features/dashboard/DashboardControls';
import { DashboardLayoutMenu } from '../features/dashboard/DashboardLayoutMenu';
import { dashboardSearch, resolveRange, type DashboardSearch } from '../features/dashboard/period';
import {
  ArrearsTotalTile,
  InspectionsTile,
  LeaseEndsTile,
  OccupancyTile,
} from '../features/dashboard/ManagementTiles';
import { ArrearsList } from '../features/dashboard/ArrearsList';
import {
  ArrearsStagesCard,
  InspectionsDueCard,
  LeaseEndsCard,
  LettingsCard,
  NewTenanciesCard,
  OccupancyCard,
  RentReviewsCard,
  VacanciesCard,
  WorkloadCard,
} from '../features/dashboard/ManagementCards';
import { todayIso } from '../features/property/today';
import { PortfolioPanel } from '../features/portfolio/PortfolioPanel';
import { portfolioScopeLabel } from '../features/portfolio/PortfolioScopeOptions';
import { portfolioScopeFor, usePortfolioScope } from '../features/portfolio/scope';
import { resolveSession } from '../features/auth/session';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/dashboard/management')({
  // The finance overview's period, parsed by the same function — see the note on `/`.
  validateSearch: (search: Record<string, unknown>): DashboardSearch => dashboardSearch(search),
  loaderDeps: ({ search }) => dashboardSearch(search),
  loader: async ({ context, deps }) => {
    const session = await resolveSession(context.queryClient);
    const scope = portfolioScopeFor(session);

    await Promise.all([
      context.queryClient.prefetchQuery(
        trpc.dashboard.management.queryOptions({ ...deps, scope }, { staleTime: 60_000 }),
      ),
      context.queryClient.prefetchQuery(
        trpc.property.portfolio.queryOptions({ scope }, { staleTime: 60_000 }),
      ),
    ]);
  },
  component: ManagementDashboard,
});

/**
 * The property manager's overview: the houses, where the finance overview is the money.
 *
 * THE SAME PAGE AS `/` IN EVERYTHING BUT THE CARDS: the same bar with the same controls — the layout
 * menu, the period, its comparison — the portfolio tree down the left, stat tiles, charts, work lists.
 *
 * THE PERIOD MOVES TWO CARDS, and the heading over them says so. Lettings counts what began and ended
 * inside it; the occupancy trend ends where it ends. Everything else is a queue or a diary as at
 * today — an inspection overdue this morning is overdue whichever month is being looked back on.
 *
 * SCOPED TO THE READER'S PORTFOLIO — the account menu's choice, clamped on the server. The subtitle
 * names the scope the server actually applied, so a total is never read as the agency's when it is
 * one manager's.
 */
function ManagementDashboard() {
  const { scope } = usePortfolioScope();
  const navigate = useNavigate({ from: Route.fullPath });

  // Parsed again, for the reason `/` gives: `useSearch` hands back what `validateSearch` dropped.
  const params = dashboardSearch(Route.useSearch());
  const today = todayIso();

  const summary = useQuery(
    trpc.dashboard.management.queryOptions(
      { ...params, scope },
      {
        meta: { errorTitle: 'Could not load the management overview' },
        // A minute: nothing here moves by the second, and the arrears pass behind it is not free.
        staleTime: 60_000,
        // Most of the page ignores the period; it must not blank while the period changes.
        placeholderData: keepPreviousData,
      },
    ),
  );

  const data = summary.data;

  // The server's clamped answer wins over what the URL asked for — the finance overview's rule.
  const asked = resolveRange(params, today);
  const range = data?.range ?? asked;
  const comparison = data?.comparison ?? comparisonRange(range);
  const rangeLabel = formatDateRange(range);
  const awaitingPeriod = data !== undefined && (range.from !== asked.from || range.to !== asked.to);

  const chooseRange = (next: DashboardSearch) => {
    void navigate({ search: next, replace: true });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Overview"
        subtitle={`${data ? `${portfolioScopeLabel(data.scope)} · ` : ''}Diary and queues are as at today; the period below scopes lettings and occupancy`}
        actions={
          <>
            <DashboardLayoutMenu current="management" search={params} />
            <DashboardControls
              range={range}
              comparison={comparison}
              activeKey={matchingRangeKey(range, today)}
              onChange={chooseRange}
            />
          </>
        }
      />

      <div className="lg:grid lg:grid-cols-[var(--panel-width)_minmax(0,1fr)] lg:gap-4">
        {/* The same sticky column as the finance overview — see `PortfolioColumn` in ./index. */}
        <div className="sticky top-[calc(var(--topbar-height)+1rem)] hidden h-[calc(100dvh-var(--topbar-height)-4rem)] lg:block">
          <PortfolioPanel />
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          {summary.error ? (
            <LoadFailed inline retry={() => void summary.refetch()} retrying={summary.isFetching} />
          ) : null}

          {/* 1. The book at a glance. */}
          {/* Two across even on a phone: one tile per row spent the first screen on four numbers. */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
            {data ? (
              <>
                <OccupancyTile data={data} />
                <ArrearsTotalTile data={data} />
                <InspectionsTile data={data} />
                <LeaseEndsTile data={data} />
              </>
            ) : (
              [0, 1, 2, 3].map((tile) => <CardSkeleton key={tile} className="h-[6.75rem]" />)
            )}
          </div>

          {/* 2. The weeks ahead, and where the arrears sit — both as at today. */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
            {data ? (
              <>
                <WorkloadCard workload={data.workload} />
                <ArrearsStagesCard arrears={data.arrears} />
              </>
            ) : (
              [0, 1].map((card) => <CardSkeleton key={card} className="h-64" />)
            )}
          </div>

          {/* 3. The only row the period moves — said on its own heading, as on `/`. */}
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 pt-1">
            <h2 className="text-[0.9375rem] font-bold tracking-[-0.014em]">Over {rangeLabel}</h2>
            <span className="text-[0.75rem] text-ink-muted">
              compared with {formatDateRange(comparison)}
            </span>
          </div>

          <div
            aria-busy={awaitingPeriod}
            className={`grid grid-cols-1 gap-4 transition-opacity duration-[var(--duration-fast)] lg:grid-cols-2 ${
              awaitingPeriod ? 'opacity-60' : ''
            }`}
          >
            {data ? (
              <>
                <OccupancyCard months={data.occupancy} />
                <LettingsCard
                  lettings={data.newTenancies}
                  rangeLabel={rangeLabel}
                  windowDays={rangeDays(range)}
                />
              </>
            ) : (
              [0, 1].map((card) => <CardSkeleton key={card} className="h-64" />)
            )}
          </div>

          {/* 4. What needs me this week. */}
          <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
            {data ? (
              <>
                <InspectionsDueCard
                  inspections={data.inspections}
                  today={data.today}
                  days={data.windows.inspectionDays}
                />
                <ArrearsList
                  rows={data.arrears.rows}
                  totalCents={data.arrears.totalCents}
                  tenancyCount={data.arrears.count}
                />
              </>
            ) : (
              [0, 1].map((card) => <CardSkeleton key={card} className="h-64" />)
            )}
          </div>

          {/* 5. What is coming. */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {data ? (
              <>
                <LeaseEndsCard
                  leaseEnds={data.leaseEnds}
                  today={data.today}
                  days={data.windows.leaseEndDays}
                />
                <RentReviewsCard reviews={data.rentReviews} days={data.windows.rentReviewDays} />
                <NewTenanciesCard
                  lettings={data.newTenancies}
                  today={data.today}
                  rangeLabel={rangeLabel}
                />
                <VacanciesCard portfolio={data.portfolio} today={data.today} />
              </>
            ) : (
              [0, 1, 2, 3].map((card) => <CardSkeleton key={card} className="h-64" />)
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** A card-shaped placeholder — DESIGN_SYSTEM §10 bans a page spinner. */
function CardSkeleton({ className }: { className: string }) {
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse rounded-lg bg-surface shadow-card ${className}`}
    />
  );
}
